import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Plus, X, Lock, Clock, AlertTriangle, TrendingUp } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { TonyFormula } from "@/components/TonyFormula";
import api from "@/lib/api";

const TIMING = {
  "creatine-monohydrate": "post", "whey-protein-isolate": "post", "beta-alanine": "pre",
  caffeine: "pre", "citrulline-malate": "pre", "beetroot-nitrate": "pre",
  "ashwagandha-ksm66": "night", "omega-3-fish-oil": "morning", "vitamin-d3": "morning",
  bcaa: "pre", tribulus: "morning", dmaa: "pre",
};
const SLOTS = { morning: "Morning", pre: "Pre-Workout", post: "Post-Workout", night: "Night" };

export default function Formula() {
  const { t } = useLang();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stack, setStack] = useState([]);
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => { api.get("/ingredients").then(({ data }) => setItems(data.ingredients || [])); }, []);

  const add = (slug) => { if (!stack.includes(slug)) setStack([...stack, slug]); setAnalysis(null); };
  const remove = (slug) => { setStack(stack.filter((s) => s !== slug)); setAnalysis(null); };

  const analyze = () => {
    const chosen = items.filter((i) => stack.includes(i.slug));
    const timing = {};
    chosen.forEach((i) => {
      const slot = TIMING[i.slug] || "morning";
      (timing[slot] ||= []).push(i.name);
    });
    const goalCount = {};
    chosen.forEach((i) => (i.goals || []).forEach((g) => (goalCount[g] = (goalCount[g] || 0) + 1)));
    const overlaps = Object.entries(goalCount).filter(([, n]) => n >= 3).map(([g]) => g);
    const covered = new Set(chosen.flatMap((i) => i.goals || []));
    const gaps = ["strength", "endurance", "energy", "recovery"].filter((g) => !covered.has(g));
    setAnalysis({ timing, overlaps, gaps });
  };

  const hasAnnual = user?.is_pro && user?.plan === "yearly";
  if (!hasAnnual) {
    return (
      <div className="mx-auto max-w-4xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("formula.title")}</h1>
          <p className="mt-1 text-sm text-gray-600">{t("formula.sub")}</p>
        </div>
        <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-8">
          <div className="pointer-events-none select-none blur-sm">
            <div className="mb-4 h-4 w-40 rounded bg-gray-200" />
            <div className="grid gap-3 sm:grid-cols-2">
              {["Post-Workout", "Pre-Workout", "Morning", "Night"].map((s) => (
                <div key={s} className="rounded-xl border border-gray-100 p-4">
                  <div className="mb-2 h-3 w-24 rounded bg-emerald-200" />
                  <div className="h-3 w-32 rounded bg-gray-200" />
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 backdrop-blur-[2px]">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-amber-600"><Lock size={26} /></div>
            <p className="mb-4 max-w-xs text-center text-sm font-semibold text-gray-700">{t("formula.lock")}</p>
            <Link to="/pricing" data-testid="formula-upgrade-button" className="rounded-full bg-emerald-500 px-6 py-3 text-sm font-bold text-white hover:bg-emerald-600">{t("pubmed.upgrade")}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("formula.title")}</h1>
        <p className="mt-1 text-sm text-gray-600">{t("formula.sub")}</p>
      </div>

      <TonyFormula />

      <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-gray-400">{t("formula.add")}</h3>
          <div className="flex flex-wrap gap-2">
            {items.map((i) => (
              <button key={i.slug} data-testid={`formula-add-${i.slug}`} onClick={() => add(i.slug)} disabled={stack.includes(i.slug)}
                className="flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:border-emerald-300 hover:bg-emerald-50 disabled:opacity-40">
                <Plus size={13} /> {i.name}
              </button>
            ))}
          </div>

          <h3 className="mb-3 mt-6 text-sm font-bold uppercase tracking-wider text-gray-400">{t("formula.yourStack")}</h3>
          {stack.length === 0 ? (
            <p className="text-sm text-gray-500">{t("formula.empty")}</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="formula-stack-list">
              {stack.map((slug) => {
                const it = items.find((x) => x.slug === slug);
                return (
                  <span key={slug} className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                    {it?.name}
                    <button onClick={() => remove(slug)} className="text-emerald-500 hover:text-emerald-700"><X size={13} /></button>
                  </span>
                );
              })}
            </div>
          )}
          <button data-testid="formula-analyze-button" onClick={analyze} disabled={stack.length === 0}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 py-3 text-sm font-bold text-white transition-colors hover:bg-emerald-600 disabled:opacity-50">
            <Sparkles size={16} /> {t("formula.analyze")}
          </button>
        </div>

        <div>
          {analysis ? (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="formula-results">
              <div className="rounded-2xl border border-gray-200 bg-white p-6">
                <p className="mb-3 flex items-center gap-1.5 text-sm font-bold text-gray-800"><Clock size={15} /> {t("formula.timing")}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {Object.keys(SLOTS).map((slot) => (
                    <div key={slot} className="rounded-xl border border-gray-100 p-4">
                      <p className="mb-1 text-xs font-bold uppercase tracking-wider text-emerald-600">{SLOTS[slot]}</p>
                      <p className="text-sm text-gray-600">{(analysis.timing[slot] || []).join(", ") || "—"}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white p-6">
                <p className="mb-2 flex items-center gap-1.5 text-sm font-bold text-gray-800"><AlertTriangle size={15} /> {t("formula.overlaps")}</p>
                <p className="text-sm text-gray-600">{analysis.overlaps.length ? analysis.overlaps.map((g) => t(`goal.${g}`)).join(", ") : "—"}</p>
                <p className="mb-2 mt-4 flex items-center gap-1.5 text-sm font-bold text-gray-800"><TrendingUp size={15} /> {t("formula.gaps")}</p>
                <p className="text-sm text-gray-600">{analysis.gaps.length ? analysis.gaps.map((g) => t(`goal.${g}`)).join(", ") : "—"}</p>
              </div>
            </motion.div>
          ) : (
            <div className="flex h-full min-h-[240px] items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white/50 p-6 text-center text-sm text-gray-400">
              {t("formula.empty")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
